import {Component, Plugins} from '@packages/systems/wfdl';
import {QualifiedMap} from '@packages/systems/wfdl-Qualified';
import {getNodeMaps} from '@packages/systems/core/utils/JsonDomUtils';

import type {JSONNode} from '@packages/shared/types/JSONNode';

export type Collection = {
  [key: string]: {
    _id: string;
    slug: string;
    fields: Array<{
      id: string;
      slug: string;
      type: string;
      validations?: Record<string, any>;
    }>;
    plugin?: string;
  };
};

export type PageInfo = {
  pageId: string;
  // id of the associated collection if applicable (template page)
  dynCollection: string | null;
};

export type ExtraContext = {
  symbolJsonNodes?: JSONNode[];
  validPageIds?: Array<string>;
  collections?: Collection;
  pluginComponents?: Component.ComponentMap;
  plugins?: Plugins.PluginsType;
  pageInfo: PageInfo | null;
  reportWarning?: (msg: string, warning: string) => void;
  /**
   * Data types available during migration (user components, plugin components, atoms).
   * For user components, this must represent a snapshot derived from symbolJsonNodes
   * before any patches are applied, so instance-level migrations read the original
   * component root data types instead of partially migrated values.
   */
  dataTypes: Plugins.DataTypes;
};

type EnhancedExtraContextShape = {
  symbolNodeMap?: Map<string, JSONNode>;
} & ExtraContext;

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type EnhancedExtraContext = EnhancedExtraContextShape;

const emptyDataTypes: Plugins.DataTypes = QualifiedMap.empty();

export const emptyExtraContext: ExtraContext = Object.freeze({
  pageInfo: null,
  dataTypes: emptyDataTypes,
});
export const emptyEnhancedExtraContext: EnhancedExtraContext = Object.freeze({
  pageInfo: null,
  dataTypes: emptyDataTypes,
});

export const enhanceExtraContext = (
  extraContext: ExtraContext = emptyExtraContext
): EnhancedExtraContext => {
  const clonedExtraContext: EnhancedExtraContextShape = {...extraContext};

  // If passing in a symbolTree for extra context, give it a nodeMap for optimized lookups in the patch
  if (clonedExtraContext.symbolJsonNodes) {
    const {nodeMap} = getNodeMaps(clonedExtraContext.symbolJsonNodes);
    clonedExtraContext.symbolNodeMap = nodeMap;
  }

  // Make sure we don't mutate the extra context passed in.
  return clonedExtraContext;
};
